import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { userAuth } from '../api/auth'

// import { FacebookAuthProvider, getAuth , GoogleAuthProvider,  OAuthProvider,  signInWithPopup, signOut } from "@firebase/auth";
// import { useAuthState } from 'react-firebase-hooks/auth';
// import firebase from '../api/firebase';

import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { FirebaseAuthentication, Persistence } from '@capacitor-firebase/authentication'

import { isUpdateRequired } from '../functions/versionCompare'
import { initializeEventTypes } from '../types/EventTypes'

const useAuthentication = () => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [firebaseReady, setFirebaseReady] = useState<boolean>(false)
  const [loginError, setLoginError] = useState<boolean>(false)
  const [isAccountDeleted, setIsAccountDeleted] = useState<boolean>(false)
  const [rememberMe, setRememberMe] = useState<boolean>(true)
  const [showForceUpdate, setShowForceUpdate] = useState<boolean>(false)
  const queryClient = useQueryClient()

  useEffect(() => {
    const asyncFunc = async () => {
      const rememberMe = (await localStorage.getItem('rememberMe')) === 'true'
      setRememberMe(rememberMe)
    }
    asyncFunc()
  }, [])

  useEffect(() => {
    localStorage.setItem('rememberMe', rememberMe ? 'true' : 'false')
  }, [rememberMe])

  const logout = async () => {
    try {
      await FirebaseAuthentication.signOut()
      setUser(null)
      queryClient.invalidateQueries('auth')
      queryClient.invalidateQueries('getProfile')
      setIsLoading(false)
    } catch (error) {
      console.error('Sign-Out Error:', error)
      setIsLoading(false)
    }
  }

  const setPersistence = () => {
    FirebaseAuthentication.setPersistence({
      persistence: rememberMe ? Persistence.BrowserLocal : Persistence.BrowserSession,
    })
      .then(() => {
        console.log('Persistence Set')
      })
      .catch((error) => {
        console.log(error)
      })
  }

  useEffect(() => {
    const fetchUser = async () => {
      setIsLoading(true)
      try {
        const currentUser = await FirebaseAuthentication.getCurrentUser()
        if (currentUser?.user) {
          queryClient.invalidateQueries('auth')
        }
      } catch (error) {
        console.error('Get Current User Error:', error)
      } finally {
        setFirebaseReady(true)
        setIsLoading(false)
      }
    }
    fetchUser()
  }, [queryClient])

  const getAccessToken = async () => {
    const result = await FirebaseAuthentication.getIdToken()
    return result.token
  }

  const authQuery = useQuery(['auth'], userAuth, {
    enabled: firebaseReady,
    retry: (failureCount, error: any) => {
      // Don't retry on auth failures (401/403) — retrying won't help
      const status = error?.response?.status
      if (status === 401 || status === 403) return false
      return failureCount < 3
    },
    refetchOnWindowFocus: false,
    onSuccess: async (res) => {
      setIsLoading(false)
      setUser(res)
      console.log(res)
      console.log('User Authenticated!')
      console.log('User Information: ', res)
      initializeEventTypes()

      // Check for force update requirement
      if (res?.minimum_app_version) {
        try {
          // Only check on mobile platforms (ios/android), skip on web
          const platform = Capacitor.getPlatform()
          if (platform === 'ios' || platform === 'android') {
            const appInfo = await App.getInfo()
            const currentVersion = appInfo.version || '0.0.0'
            const minimumVersion = res.minimum_app_version

            console.log('Version check:', {
              currentVersion,
              minimumVersion,
              platform,
            })

            if (isUpdateRequired(currentVersion, minimumVersion)) {
              console.log('Update required!')
              setShowForceUpdate(true)
            } else {
              setShowForceUpdate(false)
            }
          }
        } catch (error) {
          console.error('Error checking app version:', error)
          // If version check fails, don't block the user
          setShowForceUpdate(false)
        }
      } else {
        // No minimum version specified, allow access
        setShowForceUpdate(false)
      }
    },
    onError: (error: any) => {
      setUser(null)
      console.log('User Not Authenticated!')
      setIsLoading(false)
      setShowForceUpdate(false)
      
      // Check if the error is due to deleted account
      const errorMessage = error?.response?.data?.message || ''
      const statusCode = error?.response?.data?.status_code || error?.response?.status
      if (errorMessage === 'User account has been deleted' && statusCode === 401) {
        setIsAccountDeleted(true)
        // Sign out from Firebase since the account is deleted
        FirebaseAuthentication.signOut().catch(console.error)
      } else {
        setIsAccountDeleted(false)
      }
    },
  })

  const signInWithEmailAndPassword = async (email: string, password: string) => {
    setIsLoading(true)
    setIsAccountDeleted(false) // Reset deleted account state on new login attempt

    setPersistence()

    FirebaseAuthentication.signInWithEmailAndPassword({
      email: email,
      password: password,
    })
      .then(async (result) => {
        setLoginError(false)
        setIsAccountDeleted(false)
        console.log(result)
        await FirebaseAuthentication.getIdToken({ forceRefresh: true })
        queryClient.invalidateQueries('auth')
        return result.user
      })
      .catch((error) => {
        setIsLoading(false)
        console.log(error)
        setLoginError(true)
        setIsAccountDeleted(false)
        return null
      })
  }

  const registerWithEmailAndPassword = async (email: string, password: string) => {
    setIsLoading(true)
    setPersistence()

    FirebaseAuthentication.createUserWithEmailAndPassword({
      email: email,
      password: password,
    })
      .then(async (result) => {
        console.log(result)
        await FirebaseAuthentication.getIdToken({ forceRefresh: true })
        queryClient.invalidateQueries('auth')
        return result.user
      })
      .catch((error) => {
        setIsLoading(false)
        console.log(error)
        return null
      })
  }

  const signInWithGoogle = async () => {
    setPersistence()

    try {
      const result = await FirebaseAuthentication.signInWithGoogle()
      console.log('Google Sign-In Result:', result)
      await FirebaseAuthentication.getIdToken({ forceRefresh: true })
      queryClient.invalidateQueries('auth')
      return result.user
    } catch (error) {
      console.log('Google Sign-In Error:', error)
      return null
    }
  }
  // const provider = new GoogleAuthProvider()

  // signInWithPopup(auth, provider)
  // .then(() => {

  //     //const credential = GoogleAuthProvider.credentialFromResult(result)
  //     // const token = credential?.accessToken
  //     // const user = result.user

  //     queryClient.invalidateQueries('auth');
  // })
  // .catch(() => {

  //     // const errorCode = error.code
  //     // const errorMessage = error.message
  //     // const email = error.email
  //     // const credential = GoogleAuthProvider.credentialFromError(error)
  // });

  const signInWithFacebook = async () => {
    setPersistence()

    try {
      const result = await FirebaseAuthentication.signInWithFacebook()
      console.log('Facebook Sign-In Result:', result)
      await FirebaseAuthentication.getIdToken({ forceRefresh: true })
      queryClient.invalidateQueries('auth')
      return result.user
    } catch (error) {
      console.log('Facebook Sign-In Error:', error)
      return null
    }
  }

  const signInWithApple = async () => {
    setPersistence()

    try {
      const result = await FirebaseAuthentication.signInWithApple()
      console.log('Apple Sign-In Result:', result)
      await FirebaseAuthentication.getIdToken({ forceRefresh: true })
      queryClient.invalidateQueries('auth')
      return result.user
    } catch (error) {
      console.log('Apple Sign-In Error:', error)
      return null
    }
  }

  // const logout = () => {

  //     setIsLoading(true)

  //     signOut(getAuth(firebase))
  //     .then(() => {
  //         setUser(null)
  //         setIsLoading(false)
  //         queryClient.invalidateQueries('auth');
  //     })
  // }

  // useEffect(() => {
  //     if (authUser?.uid !== currentUid) {
  //         queryClient.invalidateQueries('auth');
  //         setCurrentUid(authUser?.uid)
  //     }
  // }, [authUser]);

  return {
    user: user,
    loading: !firebaseReady || authQuery.isLoading,
    loginLoading: isLoading,
    loginError: loginError,
    isAccountDeleted: isAccountDeleted,
    logout: logout,
    error: authQuery.isError,
    signInWithGoogle: signInWithGoogle,
    signInWithFacebook: signInWithFacebook,
    signInWithApple: signInWithApple,
    signInWithEmailAndPassword: signInWithEmailAndPassword,
    registerWithEmailAndPassword: registerWithEmailAndPassword,
    getAccessToken: getAccessToken,
    rememberMe: rememberMe,
    setRememberMe: setRememberMe,
    showForceUpdate: showForceUpdate,
  }
}

export default useAuthentication
